import i18n from "i18next"
import { initReactI18next } from "react-i18next"
import LanguageDetector from "i18next-browser-languagedetector"

const modules = import.meta.glob("./locales/**/**/*.json", {
  eager: true,
})

function loadResources() {
  const resources: any = {}

  Object.entries(modules).forEach(([path, module]: any) => {
    const [, lang, namespace] = path.match(/locales\/(.*?)\/(.*?).json/)!

    resources[lang] ??= {}
    resources[lang][namespace] = module.default
  })

  return resources
}

interface PluralRule {
  select: (count: number) => string
  resolvedOptions: () => { pluralCategories: string[] }
}

interface PluralResolverLike {
  getRule: (code: string, options?: Record<string, unknown>) => PluralRule | undefined
  clearCache: () => void
}

/**
 * "br" (a folder/language code, not a BCP-47 tag) is misread by `Intl.PluralRules` as
 * Breton, which has 5 plural categories (one/two/few/many/other) instead of Portuguese's 2.
 * Counts like 2, 3 or 4 then resolve to "_two"/"_few" suffixes that don't exist in any
 * "_one"/"_other" translation key, so i18next falls back to the raw key. Force
 * Portuguese/English-style (one/other) plural resolution for it.
 */
const applyPortuguesePluralRule = () => {
  const pluralResolver = i18n.services.pluralResolver as unknown as PluralResolverLike

  const originalGetRule = pluralResolver.getRule.bind(pluralResolver)

  const portugueseRule: PluralRule = {
    select: (count) => (count === 1 ? "one" : "other"),
    resolvedOptions: () => ({ pluralCategories: ["one", "other"] }),
  }

  pluralResolver.getRule = (code, options) =>
    code?.toLowerCase() === "br" ? portugueseRule : originalGetRule(code, options)

  pluralResolver.clearCache()
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: loadResources(),
    fallbackLng: "br",
    defaultNS: "signup",
    detection: {
      order: ["localStorage", "navigator"],
      caches: ["localStorage"],
      lookupLocalStorage: "i18nextLng",
    },
    interpolation: {
      escapeValue: false,
    },
  })
  .then(applyPortuguesePluralRule)

export default i18n
